import React from "react";
import { motion } from "motion/react";
import { Sparkles } from "lucide-react";

type LexCounselBadgeProps = {
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Faster, brighter pulse while AI is working */
  active?: boolean;
  /** Gemini-style sparkle icon for light panel */
  variant?: "default" | "gemini";
};

const SIZE_STYLES = {
  sm: "px-2 py-0.5 text-[10px] tracking-wide rounded-full",
  md: "px-3 py-1.5 text-[12px] tracking-wide rounded-xl",
  lg: "px-4 py-2.5 text-[13px] font-medium tracking-wide rounded-2xl",
} as const;

const GLASS_BLACK =
  "relative z-10 border border-white/20 bg-black/90 text-white/95 backdrop-blur-xl backdrop-saturate-150";

const EASE = [0.45, 0, 0.55, 1] as const;

/**
 * Lex Counsel label — glassmorphism on black with subtle living pulse.
 * Use variant="gemini" in the light side panel for a sparkle icon.
 */
export default function LexCounselBadge({
  size = "md",
  className = "",
  active = false,
  variant = "default",
}: LexCounselBadgeProps) {
  if (variant === "gemini") {
    return (
      <motion.span
        className={`inline-flex shrink-0 items-center justify-center text-[var(--counsel-accent)] ${className}`}
        animate={{ opacity: active ? [0.7, 1, 0.7] : 1 }}
        transition={{ duration: active ? 1.2 : 0, repeat: active ? Infinity : 0, ease: "easeInOut" }}
        aria-label="Lex Counsel"
      >
        <Sparkles className="h-4 w-4" strokeWidth={1.75} />
      </motion.span>
    );
  }

  const isLarge = size === "lg";
  const pulseDur = active ? 1.2 : isLarge ? 2.8 : 3.6;
  const scalePeak = active ? 1.05 : isLarge ? 1.04 : 1.03;
  return (
    <motion.span
      className={`relative inline-flex shrink-0 items-center justify-center whitespace-nowrap font-sans ${className}`}
      animate={{ scale: [1, scalePeak, 1] }}
      transition={{
        duration: pulseDur,
        repeat: Infinity,
        ease: EASE,
      }}
    >
      {/* Glass shell — static border (no luminance pulse) */}
      <span
        className={`inline-flex items-center justify-center ${GLASS_BLACK} ${SIZE_STYLES[size]}`}
      >
        {/* Text shimmer */}
        <motion.span
          className="bg-gradient-to-r from-white via-white/90 to-white/70 bg-clip-text text-transparent"
          style={{
            backgroundSize: "200% 100%",
            WebkitBackgroundClip: "text",
          }}
          animate={{
            backgroundPosition: active
              ? ["0% 50%", "100% 50%", "0% 50%"]
              : ["0% 50%", "80% 50%", "0% 50%"],
            opacity: active ? [0.92, 1, 0.92] : [0.88, 1, 0.88],
          }}
          transition={{
            duration: active ? 1.6 : 3.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          Lex Counsel
        </motion.span>
      </span>
    </motion.span>
  );
}
